import { FC, PropsWithChildren, useEffect, useState } from "react"
import { GridFilterAction } from "../../system/hooks/useGridFilter"
import FlexColumn from "../controls/FlexColumn"
import LotsByCommandsPanel from "./LotsByCommandsPanel"
import { useTabsContext } from "../../system/providers/tabsProvider"
import { LotsByGoodsFilteringPanel } from "../../Business/Accounting/LotsByGoods/FilteringPanel/LotsByGoodsFilteringPanel"
import { IdTableVariant } from "../../@types/enumsGlobal"
import { IKeyValuePair } from "../../Business/Reports/Shared/Interfaces/IKeyValuePair"
import { ReportFilter } from "../../Business/Reports/Shared/ReportFilter"
import { openTab } from "../../system/functions/openTab"
import { checkLockStatus } from "../../Business/CommonHelperFunctions"
import { useUserContext } from "../../system/providers/userContextProvider"
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common"
import { ICommand } from "./BaseCommandsPanel"
import { maxNumberPerPage } from "../grids/default/defaultGrid"
import _ from 'underscore'
import { GosContractDataProvider } from "../../Services/DataProviders/GosContractDataProvider"
import { useAppContext } from "../../system/providers/appContextProvider"
import { LotDataProvider } from "../../Services/DataProviders/LotDataProvider"
import renderGlobalAlert from "../../system/hooks/useGlobalAlert"

interface IDefaultLotPanelProps<TEntityViewDTO, TEntityGoodsViewDTO, TEntityDTO, TEntityGoodsDTO> {
    rootTranslation: string
    selectedIdLot?: string | undefined
    selectedIdGoods?: string | undefined
    selectedGoodsName?: string | undefined
    selectedItem?: [IGridRow | undefined, (item) => void]
    multipleSelect?: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
    selectedItems?: [IGridRow[] | [], (value: IGridRow[] | []) => void]
    dispatchGridFilter: (value: GridFilterAction) => void
    dataProvider: IDictionaryDataProvider<TEntityViewDTO, TEntityDTO>
    goodsDataProvider?: ITreeViewDictionaryDataProvider<TEntityGoodsViewDTO, TEntityGoodsDTO>
    creatorModalJsx: FC<ICreatorModalProps<TEntityDTO>>
    createGoodsModalJsx: FC<ICreatorModalProps<TEntityGoodsDTO>>
    addGosContractModalJsx?: FC<IGridSelectorModalProps>
    pluginSettings: IPluginSettings
    setViewState?: (viewState: GridStateType) => void
    idVariantTable?: IdTableVariant
    filters?: Array<ReportFilter>
    isHidePanel?: boolean
    isDefaultStore?: boolean
    buttonConfig?: ICommand[]
    gridFilter?: IGridFilter
    isDashboardPanel?: boolean
    isProAptekaEnabled?: boolean | null;
    includeGoodsGroups?: IEntitySimpleDTO[]
    onIncludeGoodsGroupsChange?: (groups: IEntitySimpleDTO[]) => void
    isLotPanel?: boolean
    isMandatoryAssortmentPanel?: boolean
    isTotalQuantity?: boolean
    isFalsificationPanel?: boolean
}

const DefaultLotsByCommandsPanel = <TEntityViewDTO, TEntityGoodsViewDTO, TEntityDTO, TEntityGoodsDTO>(props: PropsWithChildren<IDefaultLotPanelProps<TEntityViewDTO, TEntityGoodsViewDTO, TEntityDTO, TEntityGoodsDTO>>) => {
    const [viewState, setViewState] = useState<GridStateType>("view")
    const [modalWindow, setModalWindow] = useState(<></>)
    const tabsContext = useTabsContext();

    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.pluginSettings.permission as IPermission, userContext.userPermission);

    const appContext = useAppContext();
    const gosContractDataProvider = new GosContractDataProvider(appContext.coreApiService);
    const lotDataProvider = new LotDataProvider(appContext.coreApiService);

    useEffect(() => {
        if (viewState !== 'edit' && viewState !== 'return') {
            props.setViewState?.(viewState)
        }
    }, [viewState])

    useEffect(() => {
        renderModalWindow()
    }, [viewState])

    function renderModalWindow() {
        switch (viewState) {
            case "create": renderCreator(); break;
            case "edit": renderEditor(); break;
            default: setModalWindow(<></>)
        }
    }
    function renderCreator() {
        setModalWindow(
            <props.creatorModalJsx variant='create' data={undefined}
                save={(model) => props.dataProvider?.create(model, () => setViewState("refresh"))}
                cancel={() => setViewState("return")}
                lockFromPermission={lockFromPermission}
            />
        )
    }

    function renderEditor() {
        if (props.selectedIdGoods !== undefined) {
            //idGoods
            let goodsId = props.selectedIdGoods as string;
            props.goodsDataProvider?.getById(goodsId, (entity) => {
                setModalWindow(
                    <props.createGoodsModalJsx
                        variant="edit"
                        data={entity}
                        save={(model) =>
                            props.goodsDataProvider?.update(goodsId, model, () => {
                                setViewState("refresh");
                            })
                        }
                        saveCallback={(model, callback) => {
                            props.goodsDataProvider?.update(goodsId as string, model, () => {
                                callback?.(goodsId as string)
                                setViewState('refresh')
                            })
                        }}
                        cancel={() => {
                            setViewState("return");
                            setModalWindow(<></>);
                        }}
                        lockFromPermission={lockFromPermission}
                    />
                );
            });
        }
    }

    function renderAddGosContract(gosContractData, totalCount) {
        if (!props.addGosContractModalJsx) return;
        
        setModalWindow(
            <props.addGosContractModalJsx
                data={gosContractData}
                totalCount={totalCount}
                ok={(e) => {
                    if (e.idGlobal) {
                        lotDataProvider.updateGosContract(e.idGlobal, props.multipleSelect?.[0] ? props.selectedItems?.[0].map(item => item.idGlobal) : [props.selectedItem?.[0]?.idGlobal] as any, () => {
                            renderGlobalAlert({
                                variant: "info",
                                statusCode: 200,
                                title: `Товар привязан к новому ГК`
                            });
                            setModalWindow(<></>);
                            props.setViewState?.("refresh");
                        })
                    }
                }}
                cancel={() => {
                    setViewState("return");
                    setModalWindow(<></>);
                }}
            />
        )
    }

    return <>
        <FlexColumn>{
            !props.isHidePanel &&
            <LotsByCommandsPanel
                rootTranslation={props.rootTranslation}
                lotMovement={
                    {
                        onClick: () => tabsContext.openChild(
                            'lot_movement_plugin',
                            "view",
                            undefined,
                            () => setViewState("refresh"),
                            {
                                idGoods: props.selectedIdGoods,
                                goodsName: props.selectedGoodsName
                            }
                        ),
                        disabled: props.selectedIdGoods?.[0] === undefined
                    }
                }
                editGoods={{
                    disabled: props.selectedIdGoods?.[0] === undefined,
                    onClick: () => setViewState("edit")
                }}
                {...(props.addGosContractModalJsx ? {
                    add: {
                        onClick: () => {
                            const lots = props.multipleSelect?.[0] && props.selectedItems?.[0]?.length
                                ? props.selectedItems[0].map(item => ({
                                    idGoodsExternalGlobal: item.idGoodsGlobal,
                                    retailPriceIncVat: Number(item.cells.find(cell => cell.propertyName === "retailPriceIncVat")?.value) || 0
                                }))
                                : [{
                                    idGoodsExternalGlobal: props?.selectedItem?.[0]?.idGoodsGlobal,
                                    retailPriceIncVat: Number(props.selectedItem?.[0]?.cells.find(cell => cell.propertyName === "retailPriceIncVat")?.value) || 0
                                }];
                            const body: IViewBody = {
                                numberPerPage: props?.gridFilter?.numberPerPage ?? 15,
                                pageNumber: props?.gridFilter?.pageNumber ?? 1,
                                columnFilters: [],
                                paramFilter: { lots },
                            };
                            gosContractDataProvider.viewByLots(body, (data, totalCount) => {
                                renderAddGosContract(data, totalCount);
                            });
                        },
                        disabled: props.multipleSelect?.[0] ? (props.selectedItems?.[0]?.length ?? 0) === 0 : props.selectedItem?.[0] === undefined,
                    }
                } : {})}
                openLotsByLots={{
                    onClick: () =>
                        props.pluginSettings.mnemocode === 'lots_by_lots_plugin'
                            ? openTab(tabsContext, 'lots_by_goods_plugin')
                            : openTab(tabsContext, 'lots_by_lots_plugin')
                }}
                print={{
                    onClick: () => {
                        return props.idVariantTable;
                    },
                    onSubItemClick: () => {
                        const result = new Array<IKeyValuePair>();
                        switch (props.pluginSettings.mnemocode) {
                            case 'lots_by_goods_plugin':
                                if (props.selectedIdGoods != undefined) {
                                    let filter: IGridFilter = { ...props.gridFilter, numberPerPage: maxNumberPerPage, pageNumber: 1 } as IGridFilter;
                                    const found = _.find(filter.columnFilters, u => u.name === "idGoodsGlobal");
                                    if (!found) {
                                        filter.columnFilters?.push({ name: "idGoodsGlobal", operator: "Eq", value: props.selectedIdGoods })
                                    } else {
                                        found.value = props.selectedIdGoods
                                    }
                                    return filter
                                }
                                break
                            default:
                                if (props.multipleSelect?.[0] && (props.selectedItems?.[0]?.length ?? 0) > 0) {
                                    props.selectedItems![0].forEach(item => {
                                        result.push({ key: item.idGlobal, value: "" })
                                    })
                                } else if (props.selectedIdLot) {
                                    result.push({ key: props.selectedIdLot, value: "" })
                                }
                                break;
                        }
                        return result
                    },
                    filters: props?.filters
                }}
                refresh={{
                    onClick: () => {
                        props.setViewState?.('refresh')
                    }
                }}
                {...(props.multipleSelect ? {
                    groupProcessing: {
                        onClick: () => {
                            props.multipleSelect![1](!props.multipleSelect![0]);
                        },
                        value: props.multipleSelect[0],
                    }
                } : {})}
                buttonConfig={props.buttonConfig}
                permission={props.pluginSettings.permission}
            />}
            <LotsByGoodsFilteringPanel
                dispatchGridFilter={props.dispatchGridFilter}
                isHideIcon={props.isHidePanel}
                isDefaultStore={props.isDefaultStore}
                isDashboardPanel={props.isDashboardPanel}
                isProAptekaEnabled={props.isProAptekaEnabled}
                includeGoodsGroups={props.includeGoodsGroups}
                onIncludeGoodsGroupsChange={props.onIncludeGoodsGroupsChange}
                isLotPanel={props.isLotPanel}
                isMandatoryAssortmentPanel={props.isMandatoryAssortmentPanel}
                isTotalQuantity={props.isTotalQuantity}
                isFalsificationPanel={props.isFalsificationPanel}
            />
        </FlexColumn>
        {modalWindow}
    </>
}

export default DefaultLotsByCommandsPanel